'use client';

import { useState, useMemo } from 'react';
import { Download, RefreshCw, Clock, Info, FileText } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { BottomSheet } from '@/components/common/BottomSheet';
import { tournamentService } from '@/services/api/tournaments';
import { handleError } from '@/lib/utils/error';
import { formatBytes } from '@/lib/utils/format';
import { formatDistanceToNow } from 'date-fns';
import type { Tournament } from '@/lib/types/auction';
import toast from 'react-hot-toast';
import { Alert } from '@/components/common/Alert';

interface PlayerReportDialogProps {
  isOpen: boolean;
  onClose: () => void;
  tournament: Tournament;
  onAction?: () => void;
}

export function PlayerReportDialog({
  isOpen,
  onClose,
  tournament,
  onAction,
}: PlayerReportDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [regenerationMessage, setRegenerationMessage] = useState<string | null>(
    null
  );

  const canRegenerate = useMemo(() => {
    if (!tournament.playerReports?.generatedAt) return true;
    const lastGenerated = new Date(tournament.playerReports.generatedAt);
    const twelveHoursAgo = new Date(Date.now() - 12 * 60 * 60 * 1000);
    return lastGenerated < twelveHoursAgo;
  }, [tournament.playerReports]);

  const handleGenerate = async () => {
    setIsLoading(true);
    setRegenerationMessage(null);
    try {
      const response = await tournamentService.generatePlayerReports(
        tournament.id
      );
      setRegenerationMessage(
        response.message ||
          'Reports are being regenerated. Please check back in about 10 minutes.'
      );
      toast.success('Regeneration started!');
      onAction?.();
      setTimeout(() => {
        onClose();
      }, 5000); // Close dialog after 5s
    } catch (error) {
      toast.error(handleError(error, 'Failed to start report generation'));
    } finally {
      setIsLoading(false);
    }
  };

  const renderContent = () => {
    if (regenerationMessage) {
      return (
        <Alert
          type="info"
          message={regenerationMessage}
          title="Generation in Progress"
        />
      );
    }

    if (!tournament.playerReports || !tournament.playerReports.files.length) {
      return (
        <Alert
          type="info"
          message="Player reports are being generated. Please check back in about 10 minutes."
          title="Reports Not Ready"
        />
      );
    }

    return (
      <>
        <div className="space-y-4">
          {tournament.playerReports.files.map((file) => (
            <div
              key={file.fileName}
              className="flex items-center justify-between rounded-lg bg-gray-50 p-3"
            >
              <div className="flex items-center">
                <FileText className="mr-3 h-6 w-6 text-primary-500" />
                <div>
                  <p className="font-semibold text-gray-800">{file.type}</p>
                  <p className="text-sm text-gray-500">
                    {formatBytes(file.fileSize)} | {file.totalPages} pages
                  </p>
                </div>
              </div>
              <a
                href={file.url}
                download={file.fileName}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button variant="light" size="sm" leftIcon={<Download />}>
                  Download
                </Button>
              </a>
            </div>
          ))}
        </div>
        <div className="mt-6 space-y-4">
          <div className="flex items-center text-sm text-gray-500">
            <Clock className="mr-2 h-4 w-4" />
            Last generated:{' '}
            {formatDistanceToNow(
              new Date(tournament.playerReports.generatedAt),
              {
                addSuffix: true,
              }
            )}
          </div>
          <Button
            onClick={handleGenerate}
            isLoading={isLoading}
            disabled={!canRegenerate || isLoading}
            leftIcon={<RefreshCw />}
            className="w-full"
          >
            {canRegenerate ? 'Generate Latest PDF' : 'Generated recently'}
          </Button>
          {!canRegenerate && (
            <p className="flex items-center text-xs text-gray-500">
              <Info className="mr-1 h-3 w-3" />
              You can regenerate reports after 12 hours of the last generation.
            </p>
          )}
        </div>
      </>
    );
  };

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      title="Player Reports"
      className="sm:max-w-md"
    >
      {renderContent()}
    </BottomSheet>
  );
}
